import React from "react";
import { Layers } from "lucide-react";
import { t } from "../../locales";
import { formatSizeLabel, formatSize } from "../../lib/utils";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../ui/table";
import { ProcessOrderMaterialProps, ProcessOrderLeftoverProps } from "../../types/processOrder";

type Props = {
  materials: ProcessOrderMaterialProps[];
};

// mg.size is a material_sizes row ({ size, material: { name } }) — flatten
// its nested material name before handing it to formatSizeLabel.
const materialSizeLabel = (size?: ProcessOrderMaterialProps["size"]): string =>
  formatSizeLabel(size ? { size: size.size, material_name: size.material?.name } : null);

const groupLeftovers = (leftovers?: ProcessOrderLeftoverProps[]) => {
  if (!leftovers || leftovers.length === 0) return [];
  return Object.entries(
    leftovers.reduce<Record<string, number>>((acc, l) => {
      const key = String(Number(l.initial_inches));
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([v, count]) => ({ inches: Number(v), count }))
    .sort((a, b) => b.inches - a.inches);
};

// Materials/leftovers table for a cutting process order's view page.
const OrderMaterialsTable = ({ materials }: Props) => {
  if (!materials || materials.length === 0) return null;

  return (
    <div className="p-4 md:p-6">
      <h3 className="text-base font-bold text-[#113F67] mb-4 flex items-center gap-2">
        <Layers className="h-5 w-5" />
        {t.processOrders.materialDetailsTitle}
      </h3>

      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t.processOrders.table.srNo}</TableHead>
              <TableHead>{t.processOrders.table.materialSize}</TableHead>
              <TableHead className="text-right">{t.processOrders.table.totalNewPipes}</TableHead>
              <TableHead>{t.processOrders.table.usedLeftover}</TableHead>
              <TableHead>{t.processOrders.table.savedLeftover}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {materials.map((mg, i) => {
              const usedGroups = groupLeftovers(mg.used_leftovers);
              const savedGroups = groupLeftovers(mg.created_leftovers);
              return (
                <TableRow key={mg.id}>
                  <TableCell className="font-medium text-gray-500">{i + 1}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                      {materialSizeLabel(mg.size)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-700">
                    {mg.total_fresh_pipes}
                  </TableCell>
                  <TableCell>
                    {usedGroups.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {usedGroups.map((p, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-300/60 text-xs text-amber-900"
                          >
                            {p.count} × <strong className="ml-1">{formatSize(p.inches)}</strong>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {savedGroups.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {savedGroups.map((p, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-300/60 text-xs text-amber-900"
                          >
                            {p.count} × <strong className="ml-1">{formatSize(p.inches)}</strong>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {materials.map((mg, i) => {
          const usedGroups = groupLeftovers(mg.used_leftovers);
          const savedGroups = groupLeftovers(mg.created_leftovers);
          return (
            <div key={mg.id} className="border rounded-lg p-3 bg-gray-50/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-400">#{i + 1}</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                  {materialSizeLabel(mg.size)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">{t.processOrders.table.totalNewPipes}</span> <span className="font-semibold text-emerald-700">{mg.total_fresh_pipes}</span></div>
              </div>
              {usedGroups.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1.5">{t.processOrders.table.usedLeftover}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {usedGroups.map((p, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-300/60 text-xs text-amber-900"
                      >
                        {p.count} × <strong className="ml-1">{formatSize(p.inches)}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {savedGroups.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1.5">{t.processOrders.table.savedLeftover}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {savedGroups.map((p, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-300/60 text-xs text-amber-900"
                      >
                        {p.count} × <strong className="ml-1">{formatSize(p.inches)}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderMaterialsTable;
